import React, { useEffect, useRef, useState } from "react";
import { FaCalendarAlt, FaClock, FaTimes } from "react-icons/fa";
import { toast } from "react-toastify";
import { createBookAppointment } from "../api/bookAppointment";
import { fetchServicesPage } from "../api/servicesPage";

const LOCATIONS = ["Hospital - Green Park Main", "Clinic - E-12 Green Park Main"];

const EMPTY_FORM = {
  name: "",
  email: "",
  phone: "",
  service_id: "",
  location: "",
  date: "",
  time: "",
};

const inputClass =
  "w-full rounded-[8px] border border-[#D1D1D1] bg-white px-4 py-3 text-[14px] text-[#444444] outline-none transition-colors focus:border-brand-purple";

const pickerInputClass = `${inputClass} pr-10 [&::-webkit-calendar-picker-indicator]:absolute [&::-webkit-calendar-picker-indicator]:inset-0 [&::-webkit-calendar-picker-indicator]:h-full [&::-webkit-calendar-picker-indicator]:w-full [&::-webkit-calendar-picker-indicator]:cursor-pointer [&::-webkit-calendar-picker-indicator]:opacity-0`;

const openPicker = (ref) => {
  ref.current?.showPicker?.();
};

const BookAppointmentModal = ({ isOpen, onClose }) => {
  const dateRef = useRef(null);
  const timeRef = useRef(null);
  const [form, setForm] = useState(EMPTY_FORM);
  const [services, setServices] = useState([]);
  const [submitting, setSubmitting] = useState(false);

  useEffect(() => {
    if (!isOpen) return undefined;

    const handleEscape = (e) => {
      if (e.key === "Escape" && !submitting) onClose();
    };

    document.body.style.overflow = "hidden";
    window.addEventListener("keydown", handleEscape);

    return () => {
      document.body.style.overflow = "";
      window.removeEventListener("keydown", handleEscape);
    };
  }, [isOpen, onClose, submitting]);

  useEffect(() => {
    if (!isOpen) return undefined;

    let isMounted = true;

    const loadServices = async () => {
      try {
        const data = await fetchServicesPage();
        if (isMounted) {
          setServices(data.services || []);
        }
      } catch {
        if (isMounted) {
          setServices([]);
        }
      }
    };

    loadServices();

    return () => {
      isMounted = false;
    };
  }, [isOpen]);

  useEffect(() => {
    if (!isOpen) {
      setForm(EMPTY_FORM);
      setSubmitting(false);
    }
  }, [isOpen]);

  if (!isOpen) return null;

  const handleChange = (e) => {
    const { name, value } = e.target;
    setForm((prev) => ({ ...prev, [name]: value }));
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setSubmitting(true);

    try {
      const result = await createBookAppointment({
        name: form.name.trim(),
        email: form.email.trim(),
        phone_number: form.phone.trim(),
        service_id: Number(form.service_id),
        appointment_location: form.location,
        date: form.date,
        time: form.time,
      });

      toast.success(result?.message || "Appointment booked successfully.");
      setForm(EMPTY_FORM);
      onClose();
    } catch (err) {
      toast.error(err.message || "Unable to book appointment. Please try again.");
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <div
      className="fixed inset-0 z-[100] flex items-center justify-center bg-black/40 px-4 py-8"
      onClick={() => {
        if (!submitting) onClose();
      }}
      role="presentation"
    >
      <div
        className="relative w-full max-w-[480px] rounded-[32px] bg-white p-8 shadow-[0_24px_60px_rgba(0,0,0,0.18)] md:p-10"
        onClick={(e) => e.stopPropagation()}
        role="dialog"
        aria-modal="true"
        aria-labelledby="booking-modal-title"
      >
        <button
          type="button"
          onClick={onClose}
          disabled={submitting}
          className="absolute right-5 top-5 flex h-8 w-8 items-center justify-center rounded-full text-[#666666] transition-colors hover:bg-[#F5F5F5] disabled:opacity-50"
          aria-label="Close booking form"
        >
          <FaTimes className="text-[14px]" />
        </button>

        <h2
          id="booking-modal-title"
          className="text-center text-[26px] font-bold text-[#1A1A4B] md:text-[28px]"
        >
          Book Appointment
        </h2>

        <form onSubmit={handleSubmit} className="mt-7 space-y-4">
          <input
            type="text"
            name="name"
            value={form.name}
            onChange={handleChange}
            placeholder="Your Name"
            className={inputClass}
            required
            disabled={submitting}
          />
          <input
            type="email"
            name="email"
            value={form.email}
            onChange={handleChange}
            placeholder="Email"
            className={inputClass}
            required
            disabled={submitting}
          />
          <input
            type="tel"
            name="phone"
            value={form.phone}
            onChange={handleChange}
            placeholder="Phone no."
            className={inputClass}
            required
            disabled={submitting}
          />

          <select
            name="service_id"
            value={form.service_id}
            onChange={handleChange}
            className={`${inputClass} appearance-none ${!form.service_id ? "text-[#999999]" : ""}`}
            required
            disabled={submitting || services.length === 0}
          >
            <option value="" disabled>
              {services.length === 0 ? "Loading services..." : "Service"}
            </option>
            {services.map((service) => (
              <option key={service.id} value={service.id}>
                {service.title}
              </option>
            ))}
          </select>

          <select
            name="location"
            value={form.location}
            onChange={handleChange}
            className={`${inputClass} appearance-none ${!form.location ? "text-[#999999]" : ""}`}
            required
            disabled={submitting}
          >
            <option value="" disabled>
              Appointment Location
            </option>
            {LOCATIONS.map((location) => (
              <option key={location} value={location}>
                {location}
              </option>
            ))}
          </select>

          <div className="relative">
            <input
              ref={dateRef}
              type="date"
              name="date"
              value={form.date}
              onChange={handleChange}
              onClick={() => openPicker(dateRef)}
              className={`${pickerInputClass} ${!form.date ? "text-transparent" : ""}`}
              required
              disabled={submitting}
            />
            {!form.date && (
              <span className="pointer-events-none absolute left-4 top-1/2 -translate-y-1/2 text-[14px] text-[#999999]">
                dd/mm/yyyy
              </span>
            )}
            <button
              type="button"
              onClick={() => openPicker(dateRef)}
              className="absolute right-3 top-1/2 flex h-7 w-7 -translate-y-1/2 items-center justify-center text-[#999999]"
              aria-label="Select date"
              disabled={submitting}
            >
              <FaCalendarAlt className="text-[15px]" />
            </button>
          </div>

          <div className="relative">
            <input
              ref={timeRef}
              type="time"
              name="time"
              value={form.time}
              onChange={handleChange}
              onClick={() => openPicker(timeRef)}
              className={`${pickerInputClass} ${!form.time ? "text-transparent" : ""}`}
              required
              disabled={submitting}
            />
            {!form.time && (
              <span className="pointer-events-none absolute left-4 top-1/2 -translate-y-1/2 text-[14px] text-[#999999]">
                HH:MM
              </span>
            )}
            <button
              type="button"
              onClick={() => openPicker(timeRef)}
              className="absolute right-3 top-1/2 flex h-7 w-7 -translate-y-1/2 items-center justify-center text-[#999999]"
              aria-label="Select time"
              disabled={submitting}
            >
              <FaClock className="text-[15px]" />
            </button>
          </div>

          <button
            type="submit"
            disabled={submitting}
            className="mt-2 w-full rounded-[8px] bg-brand-orange py-3.5 text-[15px] font-semibold text-white shadow-cta-orange transition-opacity hover:opacity-90 disabled:cursor-not-allowed disabled:opacity-70"
          >
            {submitting ? "Booking..." : "Appointment Now"}
          </button>
        </form>
      </div>
    </div>
  );
};

export default BookAppointmentModal;
